/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import type {
  SaasAuthMfaDeviceResponse,
  SaasAuthMfaDevicesResponse,
} from '@modules/cloud-api/v1/types'

import { FETCH_MFA_DEVICES, RESET_MFA } from '../../constants/actions'
import { replaceIn } from '../../lib/immutability-helpers'

import type { ReduxState } from '@/types/redux'

export interface State {
  [userId: string]: {
    devices: SaasAuthMfaDeviceResponse[]
  }
}

type FetchAction = {
  type: 'FETCH_MFA_DEVICES'
  error: Error | null
  payload: SaasAuthMfaDevicesResponse | null
  meta: {
    userId: string
  }
}

type ResetAction = {
  type: 'RESET_MFA'
  error: Error | null
  payload: SaasAuthMfaDevicesResponse | null
  meta: {
    userId: string
  }
}

export default function mfaDevicesReducer(
  state: State = {},
  action: FetchAction | ResetAction,
): State {
  if (action.type === FETCH_MFA_DEVICES) {
    if (action.payload && !action.error) {
      const { userId } = action.meta

      return replaceIn(state, [userId], {
        devices: action.payload.mfa_devices ?? [],
      })
    }
  }

  if (action.type === RESET_MFA) {
    if (action.payload && !action.error) {
      const { userId } = action.meta

      return replaceIn(state, [userId, `devices`], [])
    }
  }

  return state
}

const _getMfaDevices = (state: State, userId: string) => state[userId]?.devices

export const getMfaDevices = (state: ReduxState, userId: string) =>
  _getMfaDevices(state.mfaDevices, userId)
